import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { ScreenTemplate, TemplateMetadata, TemplateFilters, TemplateApiResponse } from '../interfaces/template.models';
import { PRODUCT_TEMPLATE, PARTNER_TEMPLATE, MARKETPLACE_TEMPLATE, MULTI_MARKETPLACES_TEMPLATE } from '../templates/template-configs';

// Debug das importações
// console.log('TemplateService: Importações - PRODUCT_TEMPLATE:', PRODUCT_TEMPLATE);
// console.log('TemplateService: Importações - PARTNER_TEMPLATE:', PARTNER_TEMPLATE);
// console.log('TemplateService: Importações - MARKETPLACE_TEMPLATE:', MARKETPLACE_TEMPLATE);
// console.log('TemplateService: Importações - MULTI_MARKETPLACES_TEMPLATE:', MULTI_MARKETPLACES_TEMPLATE);
// console.log('TemplateService: Importações - Todos os templates importados com sucesso');

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private readonly templates = new BehaviorSubject<ScreenTemplate[]>([
    PRODUCT_TEMPLATE,
    PARTNER_TEMPLATE,
    MARKETPLACE_TEMPLATE,
    MULTI_MARKETPLACES_TEMPLATE
  ]);

  private readonly selectedTemplate = new BehaviorSubject<ScreenTemplate | null>(null);

  constructor() {
    this.initializeTemplates();
  }

  /**
   * Inicializa os templates padrão
   */
  private initializeTemplates(): void {
    
    const defaultTemplates = [PRODUCT_TEMPLATE, PARTNER_TEMPLATE, MARKETPLACE_TEMPLATE, MULTI_MARKETPLACES_TEMPLATE];

    this.templates.next(defaultTemplates);
   
    
   
  }

  /**
   * Obtém todos os templates disponíveis
   */
  getTemplates(): Observable<ScreenTemplate[]> {
    return this.templates.asObservable();
  }

  /**
   * Obtém todos os templates disponíveis (síncrono)
   */
  getTemplatesSync(): ScreenTemplate[] {
    const templates = this.templates.value;
    return templates;
  }

  /**
   * Obtém template por ID
   */
  getTemplateById(id: string): Observable<ScreenTemplate | undefined> {
    return this.templates.pipe(
      map(templates => templates.find(t => t.id === id))
    );
  }

  /**
   * Obtém templates por categoria
   */
  getTemplatesByCategory(category: string): Observable<ScreenTemplate[]> {
    return this.templates.pipe(
      map(templates => templates.filter(t => t.category === category))
    );
  }

  /**
   * Obtém metadados dos templates (para listagem)
   */
  getTemplatesMetadata(): Observable<TemplateMetadata[]> {
    return this.templates.pipe(
      map(templates => templates.map(template => ({
        id: template.id,
        name: template.name,
        description: template.description,
        category: template.category,
        componentCount: template.components.length,
        createdAt: template.createdAt || new Date(),
        updatedAt: template.updatedAt || new Date()
      })))
    );
  }

  /**
   * Filtra templates com base nos critérios fornecidos
   */
  filterTemplates(filters: TemplateFilters): Observable<ScreenTemplate[]> {
    return this.templates.pipe(
      map(templates => {
        let filtered = templates;

        // Filtro por categoria
        if (filters.category) {
          filtered = filtered.filter(t => t.category === filters.category);
        }

        // Filtro por busca
        if (filters.search) {
          const search = filters.search.toLowerCase();
          filtered = filtered.filter(t => 
            t.name.toLowerCase().includes(search) || 
            t.description.toLowerCase().includes(search)
          );
        }

        // Ordenação
        if (filters.sortBy) {
          filtered = [...filtered].sort((a, b) => {
            let aValue: any, bValue: any;

            switch (filters.sortBy) {
              case 'name':
                aValue = a.name;
                bValue = b.name;
                break;
              case 'createdAt':
                aValue = a.createdAt || new Date(0);
                bValue = b.createdAt || new Date(0);
                break;
              case 'updatedAt':
                aValue = a.updatedAt || new Date(0);
                bValue = b.updatedAt || new Date(0);
                break;
              default:
                return 0;
            }

            if (filters.sortOrder === 'desc') {
              return bValue > aValue ? 1 : -1;
            }
            return aValue > bValue ? 1 : -1;
          });
        }

        return filtered;
      })
    );
  }

  /**
   * Aplica template selecionado ao canvas
   */
  applyTemplate(templateId: string): Observable<boolean> {
    return this.getTemplateById(templateId).pipe(
      map(template => {
        if (template) {
          // Valida o template antes de aplicar
          const validation = this.validateTemplate(template);
          if (!validation.isValid) {
            throw new Error(`Template inválido: ${validation.errors.join(', ')}`);
          }
          
          this.selectedTemplate.next(template);
          return true;
        }
        return false;
      }),
      catchError(error => {
        console.error('Erro ao aplicar template:', error);
        return of(false);
      })
    );
  }

  /**
   * Valida um template antes de aplicá-lo
   */
  private validateTemplate(template: ScreenTemplate): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validação básica
    if (!template.id || !template.name || !template.description) {
      errors.push('Template deve ter ID, nome e descrição');
    }

    if (!template.components || template.components.length === 0) {
      errors.push('Template deve ter pelo menos um componente');
    }

    if (!template.gridConfig || !template.gridConfig.columns || !template.gridConfig.rows) {
      errors.push('Template deve ter configuração de grid válida');
    }

    // Validação dos componentes
    template.components?.forEach((component, index) => {
      if (!component.type) {
        errors.push(`Componente ${index + 1} deve ter um tipo válido`);
      }
      
      if (!component.props || !component.props.label) {
        errors.push(`Componente ${index + 1} deve ter propriedades e label válidos`);
      }

      if (!component.position || 
          component.position.row === undefined || 
          component.position.col === undefined) {
        errors.push(`Componente ${index + 1} deve ter posição válida`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Obtém template atualmente selecionado
   */
  getSelectedTemplate(): Observable<ScreenTemplate | null> {
    return this.selectedTemplate.asObservable();
  }

  /**
   * Limpa template selecionado
   */
  clearSelectedTemplate(): void {
    this.selectedTemplate.next(null);
  }

  /**
   * Adiciona novo template
   */
  addTemplate(template: ScreenTemplate): Observable<boolean> {
    try {
      const currentTemplates = this.templates.value;
      const newTemplate = {
        ...template,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      this.templates.next([...currentTemplates, newTemplate]);
      return of(true);
    } catch (error) {
      console.error('Erro ao adicionar template:', error);
      return of(false);
    }
  }

  /**
   * Atualiza template existente
   */
  updateTemplate(id: string, updates: Partial<ScreenTemplate>): Observable<boolean> {
    try {
      const currentTemplates = this.templates.value;
      const index = currentTemplates.findIndex(t => t.id === id);

      if (index === -1) {
        return of(false);
      }

      const updatedTemplate = {
        ...currentTemplates[index],
        ...updates,
        updatedAt: new Date()
      };

      currentTemplates[index] = updatedTemplate;
      this.templates.next([...currentTemplates]);
      return of(true);
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      return of(false);
    }
  }

  /**
   * Remove template
   */
  removeTemplate(id: string): Observable<boolean> {
    try {
      const currentTemplates = this.templates.value;
      const filtered = currentTemplates.filter(t => t.id !== id);
      
      if (filtered.length === currentTemplates.length) {
        return of(false); // Template não encontrado
      }

      this.templates.next(filtered);
      return of(true);
    } catch (error) {
      console.error('Erro ao remover template:', error);
      return of(false);
    }
  }

  /**
   * Duplica template existente
   */
  duplicateTemplate(id: string, newName?: string): Observable<ScreenTemplate | null> {
    return this.getTemplateById(id).pipe(
      map(template => {
        if (!template) {
          return null;
        }

        const duplicated = {
          ...template,
          id: `${template.id}-copy-${Date.now()}`,
          name: newName || `${template.name} (Cópia)`,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        this.addTemplate(duplicated);
        return duplicated;
      }),
      catchError(error => {
        console.error('Erro ao duplicar template:', error);
        return of(null);
      })
    );
  }

  /**
   * Exporta template para JSON
   */
  exportTemplate(id: string): Observable<string | null> {
    return this.getTemplateById(id).pipe(
      map(template => {
        if (!template) {
          return null;
        }

        try {
          return JSON.stringify(template, null, 2);
        } catch (error) {
          console.error('Erro ao exportar template:', error);
          return null;
        }
      })
    );
  }

  /**
   * Importa template de JSON
   */
  importTemplate(jsonString: string): Observable<ScreenTemplate | null> {
    try {
      const template = JSON.parse(jsonString) as ScreenTemplate;
      
      // Validação básica
      if (!template.id || !template.name || !template.components) {
        throw new Error('Template inválido');
      }

      // Gera novo ID para evitar conflitos
      template.id = `${template.id}-imported-${Date.now()}`;
      template.createdAt = new Date();
      template.updatedAt = new Date();

      this.addTemplate(template);
      return of(template);
    } catch (error) {
      console.error('Erro ao importar template:', error);
      return of(null);
    }
  }
}
